import Quill, { Delta, Parchment } from 'quill/core';
import React, { useMemo } from 'react';
import { useDebounce } from 'use-debounce';
import PostSelectOption from '../../post/PostSelectOption';
import type { UseEntitySearchConfig } from '../../search/entitySearch/useEntitySearch';
import useEntitySearch from '../../search/entitySearch/useEntitySearch';
import { usePrefixListener } from '../PrefixListener';
import type QuillEditor from '../QuillEditor';
import { ENDING_PUNCTUATIONS } from '../constants';
import PrefixTriggeredMenu from './PrefixTriggeredMenu';

const shouldAppendSpace = (quill: Quill, index: number) => {
  const [leaf, offset] = quill.getLeaf(index);

  if (leaf instanceof Parchment.TextBlot) {
    const followingChar = leaf.value()[offset];
    if (
      followingChar === ' ' ||
      (followingChar && ENDING_PUNCTUATIONS.has(followingChar))
    ) {
      return false;
    }
  }

  return true;
};

const handleMention = ({
  quill,
  text,
  id,
  type,
  mentionIndex,
  mentionContent,
}: {
  quill: Quill;
  text: string;
  id: string;
  type: string;
  mentionIndex: number;
  mentionContent: string;
}) => {
  const index = mentionIndex - text.length;

  const delta = new Delta()
    .retain(index)
    .delete(text.length)
    .insert({ mention: { id, type } }, { content: mentionContent });

  if (shouldAppendSpace(quill, mentionIndex)) {
    delta.insert(' ', quill.getFormat(index));
  }

  quill.updateContents(delta, Quill.sources.USER);
};

const USE_PREFIX_LISTENER_OPTIONS = ['@', '+'];

interface PostMentionPickerProps {
  quill: QuillEditor;
  postId: string;
  excludePostIds: Set<string>;
}

const PostMentionPicker = ({
  quill,
  postId,
  excludePostIds,
}: PostMentionPickerProps) => {
  const { clientRects, text, prefix, reset } = usePrefixListener(
    quill,
    USE_PREFIX_LISTENER_OPTIONS,
  );
  const [query] = useDebounce(text || '', 100);

  const config: UseEntitySearchConfig = useMemo(() => {
    if (!prefix) {
      return {};
    }

    return {
      post: {
        sortBy: (a, b) => {
          if (a.id === postId) return -1;
          if (b.id === postId) return 1;
          return 0;
        },
        filter: (post) => !excludePostIds.has(post.id),
      },
      order: ['post'],
    };
  }, [prefix, postId, excludePostIds]);

  const searchResults = useEntitySearch(query, config);

  if (!clientRects) return null;

  const items = searchResults
    .filter((result) => result.type === 'post')
    .map((result) => ({
      id: result.id,
      data: result,
    }));

  return (
    <PrefixTriggeredMenu<(typeof items)[number]['data']>
      quill={quill}
      onSelect={({ data }) => {
        const range = quill.getSelection(true);
        if (!range) return;

        const { name, id, type } = data;

        handleMention({
          id,
          type,
          text: prefix + text,
          mentionIndex: range.index,
          mentionContent: name,
          quill,
        });
      }}
      sections={[{ items }]}
      query={query}
      onClose={reset}
      clientRects={clientRects}
      renderItem={(data, { focused }) => (
        <PostSelectOption {...data} focused={focused} />
      )}
      testId="postMentionPicker"
    />
  );
};

export default PostMentionPicker;
